import { useState } from 'react';
import {
  Monitor, HardDrive, Shield, Clock, Wrench, ArrowRight, CheckCircle,
  MapPin, ChevronDown, Cpu, Settings, Zap, Users, Building2,
  Briefcase, Database, Laptop, Server
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../../components/layout/SEO';
import { FAQPageSchema } from '../../components/seo/FAQPageSchema';
import { ServicePageSchema } from '../../components/seo/ServicePageSchema';
import { PageLayout } from '../../components/layout/PageLayout';
import { SpotlightCard } from '../../components/ui/SpotlightCard';
import { ServiceNavigation } from '../../components/ui/ServiceNavigation';
import { LocationLinks } from '../../components/ui/LocationLinks';
import { motion } from 'framer-motion';
import { TrackedPhoneLink } from '../../components/ui/TrackedPhoneLink';
import { InlineQuoteForm } from '../../components/InlineQuoteForm';

export function ComputerRepairPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const services = [
    {
      icon: Monitor,
      title: "Hardware Diagnostics & Repair",
      description: "Fast diagnosis and repair of computer hardware issues for Boise and Meridian businesses. We fix motherboards, power supplies, displays, and components on-site.",
      features: ["Component Testing", "Board-Level Repair", "Power Issues", "Display Problems"]
    },
    {
      icon: Shield,
      title: "Virus & Malware Removal",
      description: "Same-day virus and malware removal for Treasure Valley businesses. Complete system cleanup, ransomware removal, and security hardening.",
      features: ["Ransomware Removal", "Rootkit Elimination", "Security Audit", "Future Prevention"]
    },
    {
      icon: HardDrive,
      title: "Data Recovery Services",
      description: "Emergency data recovery services for Boise, Meridian, and Nampa businesses. Professional recovery of lost, corrupted, or accidentally deleted data.",
      features: ["Failed Drive Recovery", "Deleted File Recovery", "RAID Recovery", "Emergency Service"]
    },
    {
      icon: Cpu,
      title: "Performance Optimization",
      description: "Speed up slow computers for Treasure Valley businesses through hardware upgrades, software optimization, and system tuning.",
      features: ["SSD Upgrades", "RAM Expansion", "Bloatware Removal", "Startup Optimization"]
    },
    {
      icon: Settings,
      title: "Operating System Support",
      description: "Windows installation, updates, repairs, and troubleshooting. Our local technicians resolve OS issues fast in Meridian and Boise.",
      features: ["Windows 10/11", "OS Reinstallation", "Blue Screen Fixes", "Update Issues"]
    },
    {
      icon: Server,
      title: "Business Workstation Setup",
      description: "New computer deployment, configuration, and migration services for Treasure Valley business environments.",
      features: ["Data Migration", "Software Installation", "Domain Integration", "User Setup"]
    }
  ];

  const repairProcess = [
    {
      step: 1,
      title: "Contact & Scheduling",
      description: "Call us at (208) 328-4500 or submit a service request. We schedule on-site service, often same-day."
    },
    {
      step: 2,
      title: "On-Site Diagnostics",
      description: "Our local technician arrives at your location to identify the root cause of your computer issues."
    },
    {
      step: 3,
      title: "Clear Recommendation",
      description: "We explain the problem, provide an upfront cost estimate, and discuss repair options."
    },
    {
      step: 4,
      title: "Expert Repair",
      description: "With approval, we perform repairs using quality parts and professional tools."
    },
    {
      step: 5,
      title: "Testing & Verification",
      description: "We thoroughly test all repairs to ensure consistent performance and stability."
    },
    {
      step: 6,
      title: "Documentation",
      description: "You receive detailed documentation and a 90-day warranty on our repair labor."
    }
  ];

  const industries = [
    { icon: Building2, name: "Professional Services" },
    { icon: Users, name: "Healthcare Practices" },
    { icon: Briefcase, name: "Real Estate Offices" },
    { icon: Database, name: "Financial Services" },
    { icon: Laptop, name: "Retail & Hospitality" },
    { icon: Server, name: "Manufacturing" }
  ];

  const faqItems = [
    {
      question: "How quickly can you respond to a computer repair emergency?",
      answer: "We offer same-day service for most computer repair issues during business hours (8 AM - 6 PM Monday-Friday) throughout the Treasure Valley. For critical emergencies outside business hours, our Meridian-headquartered team provides 24/7 emergency service with typical response times of 2-4 hours. Emergency service is billed at $250/hour."
    },
    {
      question: "Do you come to my business or home for computer repair?",
      answer: "Yes, we provide on-site computer repair services throughout Meridian, Boise, Eagle, Nampa, Caldwell, Kuna, Star, and the greater Treasure Valley. Our local technicians based in Meridian come directly to you. There are no travel fees within our Treasure Valley service area for repairs during business hours."
    },
    {
      question: "What does the $95/hour service rate include?",
      answer: "Our standard $95/hour rate for Treasure Valley businesses includes diagnostic testing, on-site repair labor, basic software fixes, virus/malware removal, operating system repairs, and minor hardware adjustments. Replacement parts and specialized software licenses may have additional costs, always discussed upfront."
    },
    {
      question: "Do you offer a warranty on computer repairs?",
      answer: "Yes, all our computer repair work comes with a 90-day warranty on labor. If the same issue recurs within 90 days, we fix it at no additional charge. Replacement parts carry manufacturer warranties ranging from 1-3 years."
    },
    {
      question: "Can you recover data from a crashed or damaged hard drive?",
      answer: "We offer professional data recovery services for failed hard drives, SSDs, and storage devices. For logical failures (corrupted files) we have high success rates. Physical damage may require specialized cleanroom recovery which we coordinate. We provide a free assessment to determine recoverability."
    }
  ];

  return (
    <PageLayout>
      <SEO
        title="Computer Repair Meridian & Boise | XXL IT"
        description="Business computer repair in Boise, Meridian, Nampa & Eagle. Same-day on-site service. Hardware repair, virus removal, data recovery. $95/hr. Call (208) 328-4500."
        keywords={["business computer repair Meridian", "commercial PC repair Boise", "office computer repair Idaho", "virus removal Treasure Valley"]}
        canonicalUrl="https://xxlservices.com/services/computer-repair"
      />

      <FAQPageSchema faqItems={faqItems} pageUrl="https://xxlservices.com/services/computer-repair" />
      <ServicePageSchema
        serviceName="Computer Repair Services"
        serviceDescription="Professional computer repair for Treasure Valley businesses. Hardware diagnostics, virus removal, data recovery."
        serviceType="Computer Repair and Maintenance"
        serviceOutput="Repaired computers, removed viruses, recovered data"
      />

      {/* Hero Section */}
      <section className="relative pb-20 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-300 px-4 py-2 rounded-full backdrop-blur-sm">
                <Wrench className="w-4 h-4" />
                <span className="text-sm font-medium">On-Site Service</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 text-green-300 px-4 py-2 rounded-full backdrop-blur-sm">
                <Building2 className="w-4 h-4" />
                <span className="text-sm font-medium">Business Focus</span>
              </div>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Business Computer Repair in <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Meridian & Boise</span>
            </h1>

            <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Fast, local computer repair for offices and workstations.
              Same-day on-site service, <span className="text-blue-400 font-bold">$95/hr</span> transparent pricing, and 90-day warranty.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-blue-900/30 hover:-translate-y-1"
              >
                Schedule Repair
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <TrackedPhoneLink location="computer-repair-hero" className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-white/10 transition-all duration-300">
                (208) 328-4500
              </TrackedPhoneLink>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: "Same-Day Service", value: "Available" },
                { label: "Standard Rate", value: "$95/hr" },
                { label: "Warranty", value: "90 Days" },
                { label: "Emergency", value: "24/7" }
              ].map((stat, index) => (
                <div key={index} className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl p-6">
                  <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-blue-200 text-sm font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-navy-800 border-t border-white/5 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Comprehensive Repair Services
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We handle hardware, software, and everything in between for Treasure Valley businesses.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <SpotlightCard className="h-full p-8" fromColor="from-blue-500/20" toColor="to-cyan-500/20">
                    <div className="w-14 h-14 bg-blue-900/50 border border-white/10 rounded-2xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                    <p className="text-gray-400 text-sm mb-4 leading-relaxed">{service.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((f, i) => (
                        <span key={i} className="text-xs bg-blue-500/10 text-blue-300 px-2 py-1 rounded border border-blue-500/20">{f}</span>
                      ))}
                    </div>
                  </SpotlightCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Repair Process */}
      <section className="py-24 bg-navy-900 border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Our Repair Process
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Systematic, transparent, and efficient.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {repairProcess.map((step, i) => (
              <div key={i} className="group relative">
                <div className="absolute inset-0 bg-blue-500/5 rounded-2xl transform group-hover:scale-105 transition-transform duration-300"></div>
                <div className="bg-navy-800 border border-white/10 p-8 rounded-2xl relative z-10 h-full hover:border-blue-500/30 transition-colors">
                  <div className="text-4xl font-bold text-white/5 mb-4 group-hover:text-blue-500/10 transition-colors">0{step.step}</div>
                  <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-navy-800 border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Industries We Serve
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Specialized support for business workstations across various sectors.
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <div key={i} className="bg-navy-900 border border-white/5 p-6 rounded-xl flex flex-col items-center text-center hover:bg-white/5 transition-colors">
                  <Icon className="w-8 h-8 text-blue-400 mb-4" />
                  <h3 className="text-sm font-bold text-white">{ind.name}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-navy-900 border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Common Questions
            </h2>
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <div key={index} className="bg-navy-800 border border-white/5 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="font-bold text-white pr-8">{item.question}</span>
                    <ChevronDown className={`w-5 h-5 text-blue-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6 pt-0 text-gray-400 leading-relaxed text-sm border-t border-white/5 pt-4">
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Inline Quote Form */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <InlineQuoteForm
            source="computer-repair-page"
            defaultService="IT Support"
            headline="Get a Free Computer Repair Quote"
          />
        </div>
      </section>


      <div className="container mx-auto px-4 pb-12">
        <ServiceNavigation currentService="computer-repair" />
        <LocationLinks />
      </div>
    </PageLayout>
  );
}
